import type { PartialContext } from '@ms-cloudpack/api-server';
import type { Request, Response } from '@ms-cloudpack/create-express-app';
import { makeUrl } from '@ms-cloudpack/path-string-parsing';
import { dedent } from 'ts-dedent';
import { resolve } from '@ms-cloudpack/path-utilities';
import path from 'path';
import fsPromises from 'fs/promises';
import { getInlineScript } from '@ms-cloudpack/inline-scripts';
import { reduceResourceUrl } from '@ms-cloudpack/import-map';
import { logResponse, sendErrorResponse } from './responseHelpers.js';
import { rewriteImportsFromFile } from './rewriteImports.js';

let esModuleShim: string | undefined;

/**
 * The function handles a worker request by either:
 * 1. Rewriting bare imports to use import map URLs (when rewriteWorkerImports is enabled)
 * 2. Shimming import maps using es-module-shims (fallback approach)
 *
 * It also adds the worker query parameter so the bundle server knows it has been handled,
 * while preserving other query parameters.
 *
 * It is called when the feature `enableModuleWorkers` is enabled, the worker has not been handled yet,
 * and the output file is a worker.
 *
 * This works even for workers that are not in the import map, as long as they are in the bundle. It also works
 * when the worker is requested through a blob as the request goes through the bundle server.
 */
export async function handleWorker(
  options: {
    req: Request;
    res: Response;
    /** The worker file path to read */
    workerFilePath?: string;
  },
  context: PartialContext<'session', 'importMap' | 'urls' | 'config'>,
): Promise<void> {
  const { req, res, workerFilePath } = options;
  const { session } = context;
  const { features = {} } = session.config;

  // Check if we should use the new import rewriting approach
  if (features.rewriteWorkerImports && workerFilePath) {
    return handleWorkerWithRewrite({ req, res, workerFilePath }, context);
  }

  // Fallback to es-module-shims approach
  esModuleShim ??= await getShim();

  if (!esModuleShim) {
    // If we can't get the shim, we can't handle the worker request
    return sendErrorResponse({
      res,
      status: 500,
      requestPath: req.path,
      message: 'Failed to load import map shim for worker',
    });
  }

  const { relativeImportMapPaths } = session.config.features ?? {};
  const appUrl = relativeImportMapPaths ? session.urls.appServer : undefined;

  const originalUrl = makeUrl(req.url, session.urls.bundleServer);
  // Add the worker query parameter so the bundle server knows it has ben shimmed
  originalUrl.searchParams.set('worker', 'shim');

  // Convert to relative path if relativeImportMapPaths is enabled
  const workerUrl = reduceResourceUrl(originalUrl, appUrl);

  console.debug(`Handling worker request for ${req.path}, shimmed url: ${workerUrl}`);

  logResponse({ status: 200, requestPath: req.path, message: 'Returning worker shim' });

  // The following code is based on https://github.com/guybedford/es-module-shims?tab=readme-ov-file#module-workers
  // but it uses the bundle server to avoid sending a blob and to preserve url parameters
  // it also shims the global object and process.browser for compatibility with some packages
  // We also need to set up web worker and shared worker temporary handlers right away
  // to receive messages from the main thread
  // This is necessary as there is a bug in the browser that when the handlers are
  // set up inside an async function the messages are not received.
  // In this case importShim is an async function.
  res.type('text/javascript').send(dedent`
    var pending = true;
    var messageQueue = [];
    self.onmessage = function (event) {
      if (pending) {
        messageQueue.push(event);
      }
    };
    self.onconnect = function (event) {
      if (pending) {
        messageQueue.push(event);
      }
    };

    // Shim the global object in workers
    var globalObject =
    typeof globalThis !== 'undefined'
      ? globalThis
      : typeof self !== 'undefined'
      ? self
      : typeof window !== 'undefined'
      ? window
      : typeof global !== 'undefined'
      ? global
      : {};
    var window = globalObject;

    ${await getInlineScript('defineProcess')}

    ${await getInlineScript('registerDefineFlags')}

    registerDefineFlags(window, ${JSON.stringify(session.config.define ?? {})});

    globalObject.esmsInitOptions = { shimMode: true };
    ${esModuleShim}
    importShim.addImportMap(${JSON.stringify(session.importMap)});
    importShim('${workerUrl}')
      .then(() => {
        pending = false;
        // Process the message queue
        while (messageQueue.length) {
          const message = messageQueue.shift();
          self.onmessage(message);
          self.onconnect(message);
        }
    })
      .catch(e => setTimeout(() => { throw e; }));
    `);
}

// esModuleShims must point to the non-CSP build of ES Module Shims,
// namely the `es-module-shim.wasm.js` output: es-module-shims/dist/es-module-shims.wasm.js
const esModuleShimsPackage = 'es-module-shims';
const esModuleShimsFile = 'dist/es-module-shims.wasm.js';

/**
 * Handles a worker request by rewriting bare imports to use import map URLs.
 * This approach doesn't require es-module-shims and works with service workers.
 */
async function handleWorkerWithRewrite(
  options: {
    req: Request;
    res: Response;
    workerFilePath: string;
  },
  context: PartialContext<'session', 'importMap' | 'urls' | 'config'>,
): Promise<void> {
  const { req, res, workerFilePath } = options;
  const { session } = context;

  console.debug(`Handling worker request with import rewriting for ${req.path}`);

  try {
    const rewrittenSource = await rewriteImportsFromFile(workerFilePath, context);

    // Inject define flags (like process.browser) at the top - only needed for worker entry points
    const defineScript = await getInlineScript('defineProcess');
    const registerDefineFlags = await getInlineScript('registerDefineFlags');
    const defineInjection = dedent`
      // Shim the global object in workers
      var globalObject =
        typeof globalThis !== 'undefined'
          ? globalThis
          : typeof self !== 'undefined'
          ? self
          : typeof window !== 'undefined'
          ? window
          : typeof global !== 'undefined'
          ? global
          : {};
      var window = globalObject;

      ${defineScript}

      ${registerDefineFlags}

      registerDefineFlags(window, ${JSON.stringify(session.config.define ?? {})});

    `;

    const finalSource = defineInjection + rewrittenSource;

    logResponse({ status: 200, requestPath: req.path, message: 'Returning worker with rewritten imports' });

    // For service workers, we need to set the Service-Worker-Allowed header to allow scope "/"
    // This is not the default behavior, which restricts the scope to the worker's directory
    res.setHeader('Service-Worker-Allowed', '/');
    // Do not cache these responses as they are based on the import map at request time.
    res.setHeader('Cache-Control', 'no-cache, no-store, must-revalidate');
    res.type('text/javascript').send(finalSource);
  } catch (err) {
    const error = err as Error;
    console.error('Failed to rewrite worker imports for', req.path + ':', error);
    return sendErrorResponse({
      res,
      status: 500,
      requestPath: req.path,
      message: `Failed to rewrite worker imports: ${error.message}`,
    });
  }
}

/** Get the `es-module-shims` WASM build file contents */
async function getShim(): Promise<string | undefined> {
  const esModuleShimsPath = await resolve(esModuleShimsPackage, import.meta.dirname);
  if (!esModuleShimsPath) {
    return;
  }
  const esModuleShimsWasm = path.join(esModuleShimsPath, esModuleShimsFile);
  try {
    return await fsPromises.readFile(esModuleShimsWasm, 'utf8');
  } catch {
    // ignore
  }
}
